import { ThemeEvents } from '@theme/events';
import { sectionRenderer, normalizeSectionId } from '@theme/section-renderer';

/** @type {AbortController | null} */
let activeRefresh = null;

/** @returns {HTMLElement | null} */
function findReviewSection() {
  const widget = document.querySelector('#judgeme_product_reviews');
  if (!(widget instanceof HTMLElement) || widget.closest('.product-information, .product-details')) {
    return null;
  }

  const section = widget.closest('.shopify-section');
  if (!(section instanceof HTMLElement) || normalizeSectionId(section.id).endsWith('__main')) {
    return null;
  }

  return section;
}

/** @param {Element} section */
function runJudgeMeInlineScripts(section) {
  for (const script of section.querySelectorAll('script:not([src])')) {
    const content = script.textContent?.trim();
    if (!content?.includes('jdgm')) continue;
    try {
      new Function(content)();
    } catch {
      /* Judge.me data script is optional fallback */
    }
  }
}

/** @param {HTMLElement} widget */
function prepareHorizonWidget(widget) {
  widget.closest('.shopify-app-block')?.classList.add('jdgm-horizon-widget');
  widget.classList.remove('jdgm--done-setup-widget', 'jdgm-widget-revamp');
  widget.removeAttribute('data-v-app');
  widget.querySelector('.jdgm-legacy-widget-content')?.style.setProperty('display', 'none');
  widget.querySelectorAll('.jdgm--done-setup').forEach((el) => el.classList.remove('jdgm--done-setup'));
  widget.querySelectorAll('.jdgm-temp-hiding-style').forEach((el) => el.remove());
}

/** @param {HTMLElement} widget @returns {Promise<void>} */
function loadHorizonReviewWidget(widget) {
  const jdgm = window.jdgm;
  const entryPoint = widget.dataset.entryPoint;
  if (!jdgm?.CDN_BASE_URL || !entryPoint) return Promise.resolve();

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = `${jdgm.CDN_BASE_URL}${entryPoint}?product_id=${widget.dataset.productId || widget.dataset.id}`;
    script.onload = script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

/** @param {HTMLElement} widget @returns {Promise<void>} */
function waitForModernWidget(widget) {
  return new Promise((resolve) => {
    let frames = 0;
    const check = () => {
      if (
        widget.classList.contains('jdgm-widget-revamp') ||
        widget.hasAttribute('data-v-app') ||
        widget.querySelector('.jm-review-widget') ||
        ++frames >= 180
      ) {
        resolve();
        return;
      }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

/** @param {HTMLElement} section @param {HTMLElement} widget */
async function hydrateReviewWidget(section, widget) {
  runJudgeMeInlineScripts(section);
  prepareHorizonWidget(widget);
  await loadHorizonReviewWidget(widget);
  await waitForModernWidget(widget);
}

/** @param {import('@theme/events').VariantUpdateEvent} event */
async function onVariantUpdate(event) {
  const { newProduct } = event.detail.data;
  if (!newProduct?.url) return;

  const section = findReviewSection();
  if (!section?.id) return;

  activeRefresh?.abort();
  activeRefresh = new AbortController();
  const { signal } = activeRefresh;

  const sectionId = normalizeSectionId(section.id);
  const url = new URL(newProduct.url, window.location.origin);

  try {
    const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
    if (signal.aborted) return;

    const newSection = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById(section.id);

    if (!(newSection instanceof HTMLElement)) return;

    section.replaceWith(newSection);

    const widget = findReviewSection()?.querySelector('#judgeme_product_reviews');
    if (widget instanceof HTMLElement) {
      await hydrateReviewWidget(newSection, widget);
    }
  } catch {
    /* Keep existing widget if refresh fails */
  }
}

document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);
